import {StyleSheet, css} from 'aphrodite';
import React from 'react';

import {ActionOrigin} from 'constants/AnalyticsConstants';
import {FormActionField, ProcessTaskSchema} from 'forms/form-designer/Constants';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import notifications from 'notifications';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {confirm} from 'util/Modals';

import FormDesignerActionSelector from './FormDesignerActionSelector';
import {FormDesignerFieldsTable} from './FormDesignerFieldsTable';
import FormDesignerSectionsTable from './FormDesignerSectionsTable';
import {FormDesignerStateActions, FormDesignerState} from './FormDesignerState';
import {fetchFormActionForSchema} from './graphql/FormDesignerQueries';

export const CREATE_TASK_ACTION = {
  name: 'Create a workflow task',
  identifier: 'create_task',
  version: 0,
};

export interface FormDesignerProps {
  actionToSchemas: {[key: string]: Array<ProcessTaskSchema>};
  state: FormDesignerState;
  setState: React.Dispatch<React.SetStateAction<FormDesignerState>>;
  /** Prevent the user from adding additional actions */
  lockActions?: boolean;

  // Data for analytics payload
  formDefinitionId: string | null;
  sessionId: string;
  actionOrigin: ActionOrigin;
}

export default function FormDesigner({
  actionToSchemas,
  state,
  setState,
  lockActions,
  formDefinitionId,
  sessionId,
  actionOrigin,
}: FormDesignerProps): JSX.Element {
  const {sections, actionMap} = state;

  const onRemoveSection = (sectionKey: string): void => {
    // if the section has no fields, then we can delete without showing a confirmation modal
    const isSectionEmpty = !FormDesignerStateActions.getFields(state).some(
      (field) => field.sectionKey === sectionKey
    );
    if (isSectionEmpty) {
      setState(FormDesignerStateActions.removeSection(sectionKey));
    } else {
      confirm(
        'Delete section?',
        'The fields in this section will be moved to a different section.',
        ButtonStyle.NEGATIVE,
        {
          enableRedesign: true,
          acceptText: 'Delete section',
          onSubmit: () => {
            setState(FormDesignerStateActions.removeSection(sectionKey));
          },
        }
      );
    }
  };

  return (
    <div>
      <div>
        {[...actionMap.entries()].map(([actionKey, formAction], index) => (
          <React.Fragment key={actionKey}>
            <FormDesignerActionSelector
              formAction={formAction}
              actions={[CREATE_TASK_ACTION]}
              schemas={
                (formAction.selectedAction?.identifier &&
                  actionToSchemas[formAction.selectedAction?.identifier]) ||
                []
              }
              onSetAction={(action) => {
                setState(FormDesignerStateActions.selectAction(actionKey, action));
              }}
              onSetSchema={async (schema) => {
                setState(FormDesignerStateActions.selectSchema(actionKey, schema));
                const selectedActionWithSchemaOrInaccessible = await fetchFormActionForSchema(
                  CREATE_TASK_ACTION.identifier,
                  schema.id
                );
                if (isInaccessible(selectedActionWithSchemaOrInaccessible)) {
                  notifications.warn('You do not have permission to the selected schema');
                } else {
                  setState(
                    FormDesignerStateActions.loadAction(actionKey, selectedActionWithSchemaOrInaccessible)
                  );
                }
              }}
              onRemoveAction={
                lockActions
                  ? undefined
                  : (actionKey) => {
                      setState(FormDesignerStateActions.removeAction(actionKey));
                    }
              }
              disableAction={formAction.lockAction}
              disableSchema={formAction.lockSchema}
            />
            {formAction.selectedAction && formAction.selectedSchema && (
              <>
                {index === 0 && ( // we only render the sections table for the first action instance because it's shared otherwise
                  <FormDesignerSectionsTable
                    sections={sections}
                    onAddSection={() => {
                      setState(FormDesignerStateActions.addSection());
                    }}
                    onRemoveSection={onRemoveSection}
                    onUpdateSection={(sectionKey, sectionUpdate) => {
                      setState(FormDesignerStateActions.updateSection(sectionKey, sectionUpdate));
                    }}
                    onUpdateSections={(sections) => {
                      setState(FormDesignerStateActions.updateSections(sections));
                    }}
                  />
                )}
                <FormDesignerFieldsTable
                  action={formAction}
                  sections={sections}
                  onFieldChange={(actionKey: string, updatedField: FormActionField): void => {
                    setState(FormDesignerStateActions.updateField(actionKey, updatedField));
                  }}
                  onFieldsChange={(actionKey: string, updatedFields: Array<FormActionField>): void => {
                    setState(FormDesignerStateActions.updateFields(actionKey, updatedFields));
                  }}
                  formDefinitionId={formDefinitionId}
                  actionOrigin={actionOrigin}
                  sessionId={sessionId}
                />
              </>
            )}
          </React.Fragment>
        ))}
        {lockActions || (
          <Button
            className={css(styles.addActionButton)}
            buttonStyle={ButtonStyle.TEXT}
            size={ButtonSize.SMALL}
            icon="compose"
            text="Add action"
            onClick={() => {
              setState(FormDesignerStateActions.addAction());
            }}
          />
        )}
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  addActionButton: {
    display: 'block',
    margin: '20px 0',
  },
});
